import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Fingerprint,
  KeyRound,
  ShieldAlert,
  Users,
  Smartphone,
  Wifi,
  Keyboard,
  Crown,
  RefreshCw,
  LogIn,
  Trash2,
  History,
  Clock,
  X,
} from "lucide-react";
import { createRoom, joinRoom, listRooms, deleteRoom } from "@/lib/api";
import { getStoredName, setStoredName, setSession, getSession, getVisited, removeVisited } from "@/lib/storage";

export default function Home() {
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [smRed, setSmRed] = useState("");
  const [smBlue, setSmBlue] = useState("");
  const [code, setCode] = useState("");
  const [mode, setMode] = useState("shared");
  const [killerWaits, setKillerWaits] = useState(false);
  const [writtenHints, setWrittenHints] = useState(false);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("create");

  useEffect(() => {
    const stored = getStoredName();
    setName(stored || "");
    setSmRed(stored || "");
  }, []);

  const onCreate = async (e) => {
    e.preventDefault();
    if (mode === "online") {
      if (!smRed.trim()) return toast.error("Introduce el nombre del Jefe Rojo");
    } else {
      if (!smRed.trim() || !smBlue.trim()) return toast.error("Introduce el nombre de los dos Jefes de Espías");
      if (smRed.trim().toLowerCase() === smBlue.trim().toLowerCase()) {
        return toast.error("Los dos Jefes deben tener nombres distintos");
      }
    }

    setLoading(true);
    try {
      if (mode === "online") setStoredName(smRed.trim());
      const { room, player_id } = await createRoom({
        player_name: mode === "online" ? smRed.trim() : null,
        spymaster_red_name: mode === "shared" ? smRed.trim() : null,
        spymaster_blue_name: mode === "shared" ? smBlue.trim() : null,
        killer_waits: killerWaits,
        voice_mode: !writtenHints,
        timer_seconds: timerSeconds > 0 ? Number(timerSeconds) : null,
        mode,
      });
      setSession(room.code, { player_id, name: mode === "online" ? smRed.trim() : "Jefes" });
      nav(`/room/${room.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear la sala");
    } finally {
      setLoading(false);
    }
  };

  const onJoin = async (e) => {
    e.preventDefault();
    const cc = code.trim().toUpperCase();
    const nm = name.trim();
    if (!nm) return toast.error("Introduce tu nombre");
    if (!cc) return toast.error("Introduce el código de la sala");
    doJoin(cc, nm);
  };

  const doJoin = async (roomCode, playerName) => {
    setLoading(true);
    try {
      setStoredName(playerName);
      const { room, player_id } = await joinRoom(roomCode, { player_name: playerName });
      setSession(room.code, { player_id, name: playerName });
      nav(`/room/${room.code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo unir a la sala");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="home-dossier min-h-screen flex flex-col items-center px-4 py-10 z-[2]">
      <div className="max-w-xl w-full mb-8 relative z-[1]">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-[2px] w-10 bg-white/70" />
          <span className="label-classified" data-testid="home-tagline">DOSSIER // CLASIFICADO</span>
        </div>
        <div className="absolute right-0 top-0 hidden sm:block">
          <div className="dossier-stamp">CONFIDENCIAL</div>
        </div>
        <h1 className="font-heading text-5xl sm:text-7xl leading-none uppercase">
          Código <span className="text-[color:var(--red)]">Secreto</span>
        </h1>
        <p className="mt-3 text-[color:var(--text-dim)] text-sm sm:text-base max-w-md">
          Dos equipos. Dos Jefes de Espías. 25 palabras. Un asesino. Coordina a tus agentes y descubre las identidades antes que tus rivales.
        </p>
      </div>

      <div className="w-full max-w-xl dossier-sheet">
        <div className="dossier-tabbar flex gap-0 rounded-t-3xl overflow-hidden" role="tablist">
          <TabBtn testid="tab-create" active={tab === "create"} onClick={() => setTab("create")} icon={<Fingerprint size={16} />} label="Crear Sala" />
          <TabBtn testid="tab-join" active={tab === "join"} onClick={() => setTab("join")} icon={<KeyRound size={16} />} label="Unirse" />
        </div>

        <div className="border-t border-white/10 p-6 sm:p-8 rounded-b-3xl relative">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-white/25 to-transparent" />
          {tab === "create" ? (
            <form onSubmit={onCreate} className="flex flex-col gap-6">
              <div>
                <label className="label-classified block mb-3 text-white/80">Modo de juego</label>
                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <ToggleCard testid="mode-online" active={mode === "online"} onClick={() => setMode("online")} icon={<Wifi size={18} />} title="Online" sub="1 móvil / jugador" accent="#38bdf8" />
                  <ToggleCard testid="mode-shared" active={mode === "shared"} onClick={() => setMode("shared")} icon={<Smartphone size={18} />} title="Compartido" sub="2 móviles en mesa" accent="#fbbf24" />
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-5 sm:p-6 relative overflow-hidden group hover:border-white/20 transition-colors shadow-inner">
                <div className="absolute -top-10 -right-10 opacity-5 pointer-events-none transform group-hover:scale-110 transition-transform duration-700"><Crown size={180} /></div>
                <div className="label-classified inline-flex items-center gap-2 relative z-10" style={{ color: "var(--amber)" }}>
                  <Crown size={14} /> Jefes de Espías
                </div>

                <div className="relative z-10">
                  <label className="label-classified block mb-2" style={{ color: "var(--red)" }}>Jefe Rojo</label>
                  <div className="relative">
                    <input
                      data-testid="input-sm-red"
                      className="w-full bg-black/40 border border-white/10 focus:border-[color:var(--red)] text-white rounded-xl p-3.5 pr-10 outline-none font-heading text-lg tracking-widest uppercase transition-all focus:shadow-[0_0_20px_rgba(220,38,38,0.2)] placeholder:text-white/20"
                      placeholder="NOMBRE ROJO"
                      value={smRed}
                      onChange={(e) => setSmRed(e.target.value)}
                      maxLength={20}
                    />
                    {smRed && (
                      <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" onClick={() => setSmRed("")}> <X size={16} /> </button>
                    )}
                  </div>
                </div>

                {mode === "shared" && (
                  <div className="relative z-10 animate-in slide-in-from-top-2 fade-in duration-300">
                    <label className="label-classified block mb-2" style={{ color: "var(--blue)" }}>Jefe Azul</label>
                    <div className="relative">
                      <input
                        data-testid="input-sm-blue"
                        className="w-full bg-black/40 border border-white/10 focus:border-[color:var(--blue)] text-white rounded-xl p-3.5 pr-10 outline-none font-heading text-lg tracking-widest uppercase transition-all focus:shadow-[0_0_20px_rgba(37,99,235,0.2)] placeholder:text-white/20"
                        placeholder="NOMBRE AZUL"
                        value={smBlue}
                        onChange={(e) => setSmBlue(e.target.value)}
                        maxLength={20}
                      />
                      {smBlue && (
                        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white" onClick={() => setSmBlue("")}> <X size={16} /> </button>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-1">
                <label className="label-classified block mb-3 text-white/80">Ajustes Adicionales</label>
                <div className="flex flex-col gap-3">
                  <MiniToggle
                    active={killerWaits}
                    onClick={() => setKillerWaits((v) => !v)}
                    icon={<ShieldAlert size={18} />}
                    title="Asesino te espera"
                    sub="Tocar el asesino = muerte súbita"
                    accent="#f87171"
                  />
                  <MiniToggle
                    active={writtenHints}
                    onClick={() => setWrittenHints((v) => !v)}
                    icon={<Keyboard size={18} />}
                    title="Pistas Escritas"
                    sub="Las pistas se escriben en la app"
                    accent="#fbbf24"
                  />
                  <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-white/5 bg-black/20 shadow-inner group hover:bg-white/5 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="p-2 rounded-lg bg-white/5 text-[color:var(--text-dim)] group-hover:bg-white/10 transition-colors"><Clock size={18} /></div>
                      <div>
                        <div className="font-heading text-sm sm:text-[15px] uppercase tracking-wider text-white">Reloj de arena</div>
                        <div className="text-[9px] sm:text-[10px] uppercase text-[color:var(--text-dim)] tracking-widest mt-0.5">Límite por turno (0 = sin límite)</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 bg-black/40 p-1.5 rounded-lg border border-white/5 focus-within:border-white/30 transition-colors">
                      <input
                        data-testid="input-timer"
                        type="number"
                        min={0}
                        className="bg-transparent text-white text-center w-12 font-mono text-base outline-none"
                        placeholder="0"
                        value={timerSeconds}
                        onChange={(e) => setTimerSeconds(e.target.value)}
                      />
                      <span className="text-[10px] text-[color:var(--text-dim)] font-mono pr-2">seg</span>
                    </div>
                  </div>
                </div>
              </div>

              <button data-testid="btn-create-room" className="w-full py-4 mt-2 bg-white hover:bg-gray-200 text-black font-heading text-lg uppercase tracking-[0.2em] rounded-xl transition-all active:scale-[0.98] shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] disabled:opacity-50 disabled:active:scale-100" disabled={loading} type="submit">
                {loading ? "Creando..." : "Crear Sala"}
              </button>
            </form>
          ) : (
            <form onSubmit={onJoin} className="flex flex-col gap-6">
              <div className="grid grid-cols-1 gap-4 rounded-2xl border border-white/10 bg-gradient-to-b from-white/5 to-transparent p-5 sm:p-6 relative overflow-hidden group hover:border-white/20 transition-colors shadow-inner">
                <div className="absolute -top-10 -right-10 opacity-5 pointer-events-none transform group-hover:scale-110 transition-transform duration-700"><KeyRound size={180} /></div>
                <div className="label-classified inline-flex items-center gap-2 relative z-10" style={{ color: "white" }}>
                  <Users size={14} /> Acceso de Agente
                </div>
                <div className="relative z-10">
                  <label className="label-classified block mb-2">Tu nombre</label>
                  <input data-testid="input-name-join" className="w-full bg-black/40 border border-white/10 focus:border-white/50 text-white rounded-xl p-3.5 outline-none font-heading text-lg tracking-widest uppercase transition-all focus:shadow-[0_0_20px_rgba(255,255,255,0.1)] placeholder:text-white/20" placeholder="TU NOMBRE" value={name} onChange={(e) => setName(e.target.value)} maxLength={20} />
                </div>
                <div className="relative z-10">
                  <label className="label-classified block mb-2">Código de sala</label>
                  <input data-testid="input-room-code" className="w-full bg-black/40 border border-white/10 focus:border-white/50 text-white rounded-xl p-3.5 outline-none font-heading text-2xl tracking-[0.4em] text-center uppercase transition-all focus:shadow-[0_0_20px_rgba(255,255,255,0.1)] placeholder:text-white/20" placeholder="XXXXX" value={code} onChange={(e) => setCode(e.target.value.toUpperCase().slice(0, 6))} maxLength={6} />
                </div>
              </div>
              <button data-testid="btn-join-room" className="w-full py-4 mt-2 bg-white hover:bg-gray-200 text-black font-heading text-lg uppercase tracking-[0.2em] rounded-xl transition-all active:scale-[0.98] shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] disabled:opacity-50 disabled:active:scale-100" disabled={loading} type="submit">
                {loading ? "Conectando..." : "Unirse"}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Active rooms list */}
      <ActiveRooms />

      <div className="mt-8 max-w-xl w-full">
        <div className="hr-classified mb-4" />
        <div className="grid grid-cols-3 gap-3 text-center">
          <Stat icon={<Users size={18} />} label="2 equipos" />
          <Stat icon={<Fingerprint size={18} />} label="25 pistas" />
          <Stat icon={<ShieldAlert size={18} />} label="1 asesino" />
        </div>
      </div>
    </div>
  );
}

const TabBtn = ({ testid, active, onClick, icon, label }) => (
  <button
    data-testid={testid}
    role="tab"
    onClick={onClick}
    className={`flex-1 px-4 py-4 sm:py-5 font-heading uppercase tracking-[0.15em] sm:tracking-[0.2em] text-xs sm:text-sm transition-all duration-200 relative ${active ? "text-white bg-white/5" : "text-[color:var(--text-dim)] hover:text-white"}`}
  >
    <span className="inline-flex items-center gap-2 relative z-10">{icon} {label}</span>
    {active && (
      <>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-white/35 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-[2px] bg-gradient-to-r from-transparent via-[color:var(--amber)] to-transparent" />
      </>
    )}
  </button>
);

const ActiveRooms = () => {
  const nav = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [confirmJoin, setConfirmJoin] = useState(null); // { code, name }

  const fetchRooms = async () => {
    try {
      const { rooms } = await listRooms();
      setRooms(rooms || []);
    } catch {
      // Silent fail
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRooms();
    const id = setInterval(fetchRooms, 5000);
    return () => clearInterval(id);
  }, []);

  const visited = getVisited();
  const visitedMap = Object.fromEntries(visited.map((v) => [v.code, v]));

  const onEnter = (room) => {
    const v = visitedMap[room.code];
    const stored = getStoredName();
    const suggested = (stored || v?.name || "").trim();
    setConfirmJoin({ code: room.code, name: suggested });
  };

  const doEnter = async () => {
    const code = confirmJoin?.code;
    const nm = (confirmJoin?.name || "").trim();
    if (!code) return;
    if (!nm) return toast.error("Introduce tu nombre");

    setBusy(true);
    try {
      setStoredName(nm);
      const { room: r, player_id } = await joinRoom(code, { player_name: nm });
      setSession(r.code, { player_id, name: nm });
      nav(`/room/${code}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo unir");
    } finally {
      setBusy(false);
      setConfirmJoin(null);
    }
  };

  const onDelete = async (room) => {
    const sess = getSession(room.code);
    if (!sess?.player_id) {
      toast.error("Solo el anfitrión puede eliminar la sala");
      return;
    }
    if (!window.confirm(`¿Eliminar la sala ${room.code}? Los jugadores serán expulsados.`)) return;
    setBusy(true);
    try {
      await deleteRoom(room.code, sess.player_id);
      removeVisited(room.code);
      toast.success("Sala eliminada");
      fetchRooms();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 max-w-xl w-full dossier-sheet" data-testid="active-rooms-section">
      <div className="flex items-center justify-between px-6 sm:px-8 pt-6 sm:pt-7 mb-3">
        <div className="label-classified inline-flex items-center gap-2"><History size={14} /> Salas activas</div>
        <button onClick={fetchRooms} data-testid="btn-refresh-rooms" className="text-[color:var(--text-dim)] hover:text-white">
          <RefreshCw size={14} />
        </button>
      </div>

      <div className="mx-6 sm:mx-8 mb-6 border border-[color:var(--border)] bg-black/20 divide-y divide-[color:var(--border)] rounded-2xl overflow-hidden">
        {loading ? (
          <div className="p-3 text-xs text-[color:var(--text-dim)] text-center">Cargando…</div>
        ) : rooms.length === 0 ? (
          <div className="p-4 text-xs text-[color:var(--text-dim)] text-center">No hay salas activas. Crea una y serás el primero.</div>
        ) : (
          rooms.map((room) => {
            const v = visitedMap[room.code];
            const sess = getSession(room.code);
            const showDelete = Boolean(sess?.player_id);
            return (
              <div key={room.code} data-testid={`room-row-${room.code}`} className="p-3 flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg tracking-[0.3em]">{room.code}</span>
                    <span className={`px-1.5 py-0.5 text-[9px] tracking-widest font-mono uppercase border ${room.state === "playing" ? "border-[color:var(--amber)] text-[color:var(--amber)]" : room.state === "ended" ? "border-[color:var(--green)] text-[color:var(--green)]" : "border-[color:var(--text-dim)] text-[color:var(--text-dim)]"}`}>
                      {room.state === "playing" ? "Jugando" : room.state === "ended" ? "Terminada" : "Lobby"}
                    </span>
                    {room.mode === "shared" && (
                      <span className="px-1.5 py-0.5 text-[9px] tracking-widest font-mono uppercase border border-[color:var(--text-dim)] text-[color:var(--text-dim)] inline-flex items-center gap-1">
                        <Smartphone size={10} /> Compartido
                      </span>
                    )}
                    {room.killer_waits && <span className="px-1.5 py-0.5 text-[9px] tracking-widest font-mono uppercase border border-[color:var(--red)] text-[color:var(--red)]">Asesino</span>}
                    {room.voice_mode && <span className="px-1.5 py-0.5 text-[9px] tracking-widest font-mono uppercase border border-[color:var(--amber)] text-[color:var(--amber)]">Voz</span>}
                  </div>
                  <div className="text-[10px] tracking-widest text-[color:var(--text-dim)] mt-1 truncate">
                    {room.player_count} {room.player_count === 1 ? "jugador" : "jugadores"}
                    {(room.spymaster_red_name || room.spymaster_blue_name) && (
                      <> · <span style={{ color: "var(--red)" }}>{room.spymaster_red_name || "?"}</span> vs <span style={{ color: "var(--blue)" }}>{room.spymaster_blue_name || "?"}</span></>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`btn-enter-room-${room.code}`} disabled={busy} onClick={() => onEnter(room)} className="btn-ghost text-xs py-2 inline-flex items-center gap-1">
                    {v ? <><LogIn size={12} /> Volver</> : <><LogIn size={12} /> Entrar</>}
                  </button>
                  {showDelete && (
                    <button data-testid={`btn-delete-room-${room.code}`} disabled={busy} onClick={() => onDelete(room)} className="text-[color:var(--text-dim)] hover:text-[color:var(--red)] p-2" title="Eliminar (sólo host)">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {confirmJoin && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-[color:var(--surface)] border border-[color:var(--border)] rounded-2xl p-6 max-w-sm w-full">
            <div className="label-classified mb-4">¿Cómo quieres llamarte?</div>
            <div className="flex flex-col gap-3">
              <input
                type="text"
                className="w-full bg-black/40 border border-white/10 focus:border-white/50 text-white rounded-xl p-3.5 outline-none font-heading text-lg tracking-widest uppercase"
                placeholder="TU NOMBRE"
                maxLength={20}
                value={confirmJoin.name}
                onChange={(e) => setConfirmJoin((v) => ({ ...v, name: e.target.value }))}
                autoFocus
              />
              <div className="flex gap-2">
                <button className="flex-1 py-3 bg-white text-black font-heading uppercase rounded-xl" onClick={doEnter} disabled={busy}>
                  Unirse
                </button>
                <button className="py-3 px-4 bg-white/10 text-white font-heading uppercase rounded-xl" onClick={() => setConfirmJoin(null)} disabled={busy}>
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ToggleCard = ({ testid, active, onClick, icon, title, sub, accent }) => {
  const accentColor = accent || "white";
  return (
    <button
      type="button"
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      className={`relative p-4 sm:p-5 rounded-2xl text-left transition-all duration-300 backdrop-blur-md border overflow-hidden group ${active ? "bg-white/10 shadow-lg -translate-y-0.5" : "bg-black/20 hover:bg-white/5 hover:border-white/20"}`}
      style={{ borderColor: active ? accentColor : "rgba(255,255,255,0.1)", boxShadow: active ? `0 4px 30px -5px ${accentColor}40` : undefined }}
    >
      {active && <div className="absolute inset-0 opacity-10 pointer-events-none transition-all duration-700" style={{ background: `radial-gradient(circle at top right, ${accentColor}, transparent 70%)` }} />}
      <div className="flex flex-col gap-3 sm:gap-4 relative z-10">
        <div className="flex items-center justify-between">
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center transition-colors shadow-inner bg-black/40" style={{ color: active ? accentColor : "var(--text-dim)" }}>
            {icon}
          </div>
          <div className="w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors" style={{ borderColor: active ? accentColor : "rgba(255,255,255,0.2)", background: "transparent" }}>
            {active && <div className="w-2.5 h-2.5 rounded-full shadow-[0_0_10px_currentColor]" style={{ backgroundColor: accentColor, color: accentColor }} />}
          </div>
        </div>
        <div>
          <div className="font-heading text-base sm:text-lg uppercase tracking-widest transition-colors" style={{ color: active ? accentColor : "var(--text)" }}>
            {title}
          </div>
          <div className="text-[9px] sm:text-[11px] tracking-widest mt-1 uppercase transition-colors" style={{ color: active ? `${accentColor}90` : "var(--text-dim)" }}>
            {sub}
          </div>
        </div>
      </div>
    </button>
  );
};

const MiniToggle = ({ active, onClick, icon, title, sub, accent }) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex items-center justify-between gap-4 p-4 rounded-xl border transition-all duration-300 text-left w-full group shadow-inner ${active ? "bg-white/5" : "bg-black/20 hover:bg-white/5"}`}
    style={{ borderColor: active ? accent : "rgba(255,255,255,0.05)", boxShadow: active ? `0 0 20px -5px ${accent}20` : undefined }}
  >
    <div className="flex items-center gap-4">
      <div className={`p-2 rounded-lg transition-colors shadow-inner ${active ? "bg-white/10" : "bg-white/5 group-hover:bg-white/10"}`} style={{ color: active ? accent : "var(--text-dim)" }}>
        {icon}
      </div>
      <div>
        <div className="font-heading text-sm sm:text-[15px] uppercase tracking-wider transition-colors" style={{ color: active ? accent : "white" }}>{title}</div>
        <div className="text-[9px] sm:text-[10px] uppercase tracking-widest mt-0.5 transition-colors" style={{ color: active ? `${accent}80` : "var(--text-dim)" }}>{sub}</div>
      </div>
    </div>
    <div className={`w-10 h-5 rounded-full relative transition-colors shadow-inner ${active ? "" : "bg-white/10"}`} style={{ backgroundColor: active ? accent : undefined, boxShadow: active ? `inset 0 0 5px rgba(0,0,0,0.5)` : undefined }}>
      <div className={`absolute top-[2px] left-[2px] w-4 h-4 rounded-full shadow-[0_2px_5px_rgba(0,0,0,0.5)] transition-transform duration-300 ${active ? "translate-x-5 bg-white" : "bg-gray-400"}`} />
    </div>
  </button>
);

const Stat = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-1 text-[color:var(--text-dim)]">
    <div className="text-white/80">{icon}</div>
    <span className="label-classified">{label}</span>
  </div>
);
